import axios from 'axios';
import { Bell, ChevronDown, ChevronRight, LogOut, Menu, Moon, Search, Settings as SettingsIcon, Sun, User } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import matthewAvatar from '../../assets/avatars/matthew.svg';
import { API_BASE_URL } from '../../config';
import { MENU_SIDEBAR } from '../../config/menu.config';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../firebase';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Sheet, SheetBody, SheetContent, SheetTrigger } from '../components/Sheet';
import { cn } from '../lib/utils';

/**
 * Metronic Demo1 Header Component
 * Professional header with search, notifications, and user menu
 */
export function Header() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!currentUser || !auth.currentUser) return;

    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await axios.get(`${API_BASE_URL}/api/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.data.success) {
        const count = response.data.count || 0;
        console.log('Unread count:', count);
        setUnreadCount(count);

        // If there are unread notifications but none in the list, fetch unread only
        if (count > 0 && notifications.length === 0 && !notificationsLoading) {
          console.log('Unread notifications exist but not in list, fetching unread only...');
          // Will be handled by useEffect when dropdown opens
        }
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [currentUser]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!currentUser || !auth.currentUser) {
      console.log('Notification fetch: No current user or auth');
      return;
    }

    setNotificationsLoading(true);
    try {
      const idToken = await auth.currentUser.getIdToken();

      // First try to get all notifications (recent 10)
      console.log('Notification fetch: Requesting from', `${API_BASE_URL}/api/notifications?limit=10`);

      const response = await axios.get(`${API_BASE_URL}/api/notifications?limit=10`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      // If no notifications but unread count > 0, try fetching unread only
      if (response.data.success && response.data.data.length === 0 && response.data.unread_count > 0) {
        console.log('No notifications in default list but unread_count > 0, fetching unread only...');
        const unreadResponse = await axios.get(`${API_BASE_URL}/api/notifications?limit=10&unread_only=true`, {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        console.log('Unread-only response:', unreadResponse.data);

        if (unreadResponse.data.success) {
          const unreadNotifications = unreadResponse.data.data || [];
          console.log('Notification fetch: Setting', unreadNotifications.length, 'unread notifications');
          setNotifications(unreadNotifications);
          return;
        }
      }

      console.log('Notification fetch: Response', response.data);

      if (response.data.success) {
        const notifications = response.data.data || [];
        console.log('Notification fetch: Setting', notifications.length, 'notifications');
        setNotifications(notifications);
      } else {
        console.warn('Notification fetch: Response not successful', response.data);
        setNotifications([]);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      console.error('Error details:', error.response?.data || error.message);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [currentUser]);

  // Mark notification as read
  const handleMarkAsRead = useCallback(async (notificationId) => {
    if (!currentUser || !auth.currentUser) return;

    try {
      const idToken = await auth.currentUser.getIdToken();
      await axios.post(
        `${API_BASE_URL}/api/notifications/${notificationId}/read`,
        {},
        { headers: { 'Authorization': `Bearer ${idToken}` } }
      );

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [currentUser]);

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }

    // Close dropdown
    setShowNotifications(false);

    // Navigate to contacts page with phone filter
    if (notification.candidateInfo?.phone) {
      const phone = notification.candidateInfo.phone.replace('whatsapp:', '');
      navigate(`/contacts?phone=${encodeURIComponent(phone)}`);
    } else {
      navigate('/contacts');
    }
  };

  // Fix notification names
  const handleFixNotificationNames = useCallback(async () => {
    if (!currentUser || !auth.currentUser) return;

    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await axios.post(
        `${API_BASE_URL}/api/notifications/fix-names`,
        {},
        { headers: { 'Authorization': `Bearer ${idToken}` } }
      );

      if (response.data.success) {
        console.log(
          `✅ Fixed ${response.data.updated_count} notification names`
        );
        // Refresh notifications to show updated names
        fetchNotifications();
        // Show success message
        alert(
          `Success! Updated ${response.data.updated_count} notification(s) with correct candidate names.`
        );
      }
    } catch (error) {
      console.error('Error fixing notification names:', error);
      alert('Error fixing notification names. Please try again.');
    }
  }, [currentUser, fetchNotifications]);

  // Fetch on mount and when dropdown opens
  useEffect(() => {
    if (currentUser) {
      fetchUnreadCount();
      if (showNotifications) {
        fetchNotifications();
      }
    }
  }, [currentUser, showNotifications, fetchUnreadCount, fetchNotifications]);

  // Auto-refresh unread count every 30 seconds
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
      if (showNotifications) {
        fetchNotifications();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [currentUser, showNotifications, fetchUnreadCount, fetchNotifications]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
    navigate('/login');
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Get intent icon
  const getIntentIcon = (intent) => {
    switch (intent) {
      case 'interested': return '✨';
      case 'scheduling': return '📅';
      case 'questions': return '❓';
      case 'declined': return '❌';
      case 'not_interested': return '❌';
      default: return '💬';
    }
  };

  // Get priority color (background color for left border)
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-4 border-l-red-500 bg-red-50/30';
      case 'medium': return 'border-l-4 border-l-yellow-500 bg-yellow-50/30';
      case 'low': return 'border-l-4 border-l-gray-400 bg-gray-50/30';
      default: return 'border-l-4 border-l-blue-500 bg-blue-50/30';
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now';

    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200/80 h-16 flex items-center px-4 lg:px-6">
      <div className="flex items-center justify-between w-full">
        {/* Left: Mobile Menu */}
        <div className="flex items-center gap-2">
          {/* Mobile Sidebar Toggle */}
          <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <SheetBody className="p-0">
                <MobileSidebarMenu onNavigate={() => setIsMobileSidebarOpen(false)} />
              </SheetBody>
            </SheetContent>
          </Sheet>

          {/* Breadcrumb placeholder - can add later */}
          <div className="hidden lg:block">
            <h2 className="text-sm font-semibold text-foreground">Dashboard</h2>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            shape="circle"
            onClick={toggleTheme}
            className="hover:bg-primary/10 hover:text-primary"
          >
            {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {/* Search Button */}
          <Button
            variant="ghost"
            size="icon"
            shape="circle"
            className="hover:bg-primary/10 hover:text-primary"
            onClick={() => console.log('Search clicked')}
          >
            <Search className="h-5 w-5" />
          </Button>

          {/* Notifications */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              shape="circle"
              className="hover:bg-primary/10 hover:text-primary relative"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell
                className={`h-5 w-5 transition-colors ${
                  unreadCount > 0 ? 'text-red-600' : ''
                }`}
              />
              {/* Notification badge */}
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-600 rounded-full animate-pulse border-2 border-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowNotifications(false)}
                />
                <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200/80 rounded-lg shadow-lg z-50">
                  <div className="p-4 border-b border-gray-200/80">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-foreground">Notifications</h3>
                      {unreadCount > 0 && (
                        <Badge variant="secondary">{unreadCount} new</Badge>
                      )}
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notificationsLoading ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Loading notifications...
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No notifications
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onClick={() => handleNotificationClick(notification)}
                          getIntentIcon={getIntentIcon}
                          getPriorityColor={getPriorityColor}
                          formatTimeAgo={formatTimeAgo}
                        />
                      ))
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-200/80 text-center space-y-2">
                    <Link
                      to="/contacts"
                      className="block text-sm font-medium text-primary hover:text-primary/80"
                      onClick={() => {
                        setShowNotifications(false);
                      }}
                    >
                      View All Contacts →
                    </Link>
                    {notifications.some(
                      n => n.candidateInfo?.name === 'Unknown Candidate'
                    ) && (
                      <button
                        onClick={handleFixNotificationNames}
                        className="block w-full text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        Fix Unknown Names
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <img
                src={currentUser?.photoURL || matthewAvatar}
                alt="User"
                className="h-9 w-9 rounded-full border-2 border-primary object-cover"
              />
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                />
                 <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200/80 rounded-lg shadow-lg z-50">
                  <div className="p-4 border-b border-gray-200/80">
                    <p className="font-semibold text-foreground truncate">
                      {currentUser?.displayName || 'User'}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {currentUser?.email}
                    </p>
                  </div>
                  <div className="py-2">
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <User className="h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <SettingsIcon className="h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2 text-sm text-destructive hover:bg-accent transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Log out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * Notification Item Component
 */
function NotificationItem({ notification, onClick, getIntentIcon, getPriorityColor, formatTimeAgo }) {
  const isUnread = !notification.read;
  const candidateName = notification.candidateInfo?.name || 'Unknown Candidate';
  const messagePreview = notification.messagePreview || notification.fullMessage || 'No message preview';
  const intent = notification.intent || 'general';
  const priority = notification.priority || 'medium';
  const channel = notification.channel || 'whatsapp';
  const position = notification.candidateInfo?.position || 'N/A';

  return (
    <div
      className={cn(
        "px-4 py-3 hover:bg-accent cursor-pointer transition-colors border-b border-gray-200/80 last:border-0",
        getPriorityColor(priority),
        isUnread && "bg-blue-50"
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl flex-shrink-0">{getIntentIcon(intent)}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className={cn(
              "text-sm font-semibold truncate",
              isUnread ? "text-gray-900" : "text-gray-700"
            )}>
              {candidateName}
            </p>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {channel === 'whatsapp' ? '💬' : channel === 'email' ? '📧' : channel}
            </span>
          </div>
          {position !== 'N/A' && (
            <p className="text-xs text-gray-600 mb-1 truncate">{position}</p>
          )}
          <p className="text-xs text-gray-700 mt-1 line-clamp-2">
            {messagePreview}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-500">
              {formatTimeAgo(notification.createdAt)}
            </span>
            {isUnread && (
              <Badge variant="success" className="text-[10px] px-1.5 h-4">New</Badge>
            )}
            <span className={cn(
              "text-xs px-2 py-0.5 rounded-full",
              priority === 'high' ? "bg-red-100 text-red-700" :
              priority === 'medium' ? "bg-yellow-100 text-yellow-700" :
              "bg-gray-100 text-gray-700"
            )}>
              {intent}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile Sidebar Menu
 */
function MobileSidebarMenu({ onNavigate }) {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpanded = (title) => {
    setExpandedItems(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const isActive = (path) => location.pathname === path;

  const renderMenuItem = (item, index) => {
    if (item.heading) {
      return (
        <div key={index} className="px-4 pt-5 pb-2">
          <span className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider">
            {item.heading}
          </span>
        </div>
      );
    }

    if (item.children) {
      const isExpanded = expandedItems[item.title];
      return (
        <div key={index} className="px-3">
          <button
            onClick={() => toggleExpanded(item.title)}
            className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg text-foreground hover:bg-accent"
          >
            <div className="flex items-center gap-3">
              {item.icon && <item.icon className="h-5 w-5" />}
              <span>{item.title}</span>
            </div>
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          {isExpanded && (
            <div className="ml-8 mt-1 space-y-1">
              {item.children.map((child, childIndex) => (
                <Link
                  key={childIndex}
                  to={child.path}
                  onClick={onNavigate}
                  className={cn(
                    'block px-3 py-2 text-sm rounded-lg transition-colors',
                    isActive(child.path)
                      ? 'text-primary bg-muted font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  {child.title}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={index} className="px-3">
        <Link
          to={item.path}
          onClick={onNavigate}
          className={cn(
            'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
            isActive(item.path)
              ? 'text-primary bg-muted'
              : 'text-foreground hover:bg-accent'
          )}
        >
          {item.icon && <item.icon className="h-5 w-5" />}
          <span className="flex-1">{item.title}</span>
          {item.badge && <Badge variant={item.badgeVariant || 'default'}>{item.badge}</Badge>}
        </Link>
      </div>
    );
  };

  return (
    <div className="py-4 space-y-1">
      {MENU_SIDEBAR.map((item, index) => renderMenuItem(item, index))}

      {/* Mobile Credits Widget */}
      <div className="px-4 pt-4">
        <div className="bg-primary/10 rounded-lg p-3">
          <p className="text-xs font-medium text-muted-foreground">Credits</p>
          <p className="text-2xl font-bold text-primary">150</p>
          <Link
            to="/billing"
            onClick={onNavigate}
            className="block text-center text-xs font-medium text-primary hover:text-primary/80 mt-2"
          >
            Buy More →
          </Link>
        </div>
      </div>
    </div>
  );
}

